import { useState } from "react";
import { Users, Link2 } from "lucide-react";
import AgentWorkersDialog from "./AgentWorkersDialog.jsx";
import CopyInviteLinkButton from "../agent/CopyInviteLinkButton.jsx";
import { isJobDemandSide } from "./workerLineShared.js";

/**
 * P145: what a role above an Agent can do for that Agent, from the Agent's
 * own detail drawer.
 *
 * INVITE LINK - THE HISTORY, BECAUSE IT REVERSED TWICE. P145 first added
 * "issue an invite card on this Agent's behalf", then removed it (and reverted
 * the backend permission): a CARD needs per-card setup - usage cap, expiry,
 * note, target country and language - that the owning Agent is the one
 * positioned to get right, so an admin filling that form in created more
 * coordination work than it saved.
 *
 * What is here now is not that. The owner's request was "come to the Agent's
 * name, copy the link, send" - so this issues no card of anyone's design: it
 * asks for the Agent's ONE standing link, which carries no setup at all (never
 * expires, no usage cap, no note) and is the same URL on every press. The
 * objection that killed the old button was about choices being made on the
 * Agent's behalf, and this makes none. The create form remains Agent-only.
 *
 * The link follows the Agent's SIDE, not a single hardcoded audience: a
 * worker-side line invites workers, a job-side line invites employers.
 *
 * Viewing the Agent's workers opens a dialog rather than navigating to the
 * Worker Profiles screen in the left menu: the operator is mid-task in this
 * drawer, and landing on a different top-level page reads as a wrong turn
 * rather than as an answer.
 */
export default function AgentDelegatedActionsPanel({ agentId, agentDisplayName, organizationSide, canIssueInviteLink = false }) {
  const [workersOpen, setWorkersOpen] = useState(false);

  // A job-side Agent's line is employers, not workers, so the worker invite
  // link is not a permission it lacks - it is not a thing that exists for it
  // (the backend answers WrongOrganizationSide). It gets the EMPLOYER link
  // instead, which is the one that line actually needs.
  const isJobSide = isJobDemandSide(organizationSide);

  return (
    <>
      {canIssueInviteLink && (
        <section className="wx-wl-dsec">
          <h3>
            <Link2 size={14} aria-hidden="true" /> {isJobSide ? "ลิงก์เชิญนายจ้าง" : "ลิงก์เชิญแรงงาน"}
          </h3>
          <p className="wx-wl-note-muted wx-wl-note-muted--left">
            {isJobSide
              ? "คัดลอกลิงก์ลงทะเบียนของ Agent รายนี้เพื่อส่งให้นายจ้างได้ทันที เป็นลิงก์ประจำตัวที่ใช้ซ้ำได้และไม่เปลี่ยน"
              : "คัดลอกลิงก์สมัครของ Agent รายนี้เพื่อส่งให้แรงงานได้ทันที เป็นลิงก์ประจำตัวที่ใช้ซ้ำได้และไม่เปลี่ยน"}
          </p>
          <CopyInviteLinkButton
            agentId={agentId}
            agentDisplayName={agentDisplayName}
            variant={isJobSide ? "employer" : "worker"}
            className="wx-copy-invite--full"
          />
        </section>
      )}

      <section className="wx-wl-dsec">
        <h3>
          <Users size={14} aria-hidden="true" /> แรงงานในสายงาน
        </h3>
        <p className="wx-wl-note-muted wx-wl-note-muted--left">ดูรายชื่อแรงงานที่อยู่ภายใต้ Agent รายนี้ได้ทันที โดยไม่ต้องออกจากหน้านี้</p>
        <button type="button" className="wx-wl-row-action wx-wl-row-action--full" onClick={() => setWorkersOpen(true)}>
          ดูรายชื่อแรงงานของ Agent นี้
        </button>
      </section>

      <AgentWorkersDialog open={workersOpen} agentId={agentId} agentDisplayName={agentDisplayName} onClose={() => setWorkersOpen(false)} />
    </>
  );
}
